import { MILLISECONDS_PER_SECOND } from './timers';

const SECONDS_PER_MINUTE = 60;

const DEFAULT_CONFIG_REFRESH_MINUTES = 10; // minimum minutes until configuration is refreshed
const DEFAULT_CONFIG_REFRESH_SECONDS = DEFAULT_CONFIG_REFRESH_MINUTES * SECONDS_PER_MINUTE;

export interface ICreateConfigLoader<T> {
  loadConfigFn: () => Promise<T>;
  refreshIntervalSeconds?: number;
}

/**
 * Deadline implements a simple timer reporting when it's time to refresh config.
 */
class Deadline {
  private readonly interval: number;
  private start: number;

  constructor(intervalMs: number) {
    this.start = Date.now();
    this.interval = intervalMs;
  }

  /**
   * Return true if the deadline has passed.
   *
   * @returns {boolean} isPassed
   */
  public isPassed(): boolean {
    if (this.deltaNow() > this.interval) {
      this.reset();
      return true;
    }
    return false;
  }

  private deltaNow(): number {
    const now = Date.now();
    return now - this.start;
  }

  private reset(): void {
    this.start = Date.now();
  }
}

/**
 * Return an async "getConfig" function that loads config from the provided
 * callback function.
 *
 * @param {Function} loadConfigFn
 *
 * @returns {Function} getConfigFn
 */
export function createConfigLoader<T>({
  loadConfigFn,
  refreshIntervalSeconds = DEFAULT_CONFIG_REFRESH_SECONDS,
}: ICreateConfigLoader<T>): () => Promise<T> {
  const deadline = new Deadline(refreshIntervalSeconds * MILLISECONDS_PER_SECOND);
  let config: Promise<T> | undefined;
  return async (): Promise<T> => {
    if (config && !deadline.isPassed()) {
      return config;
    }
    config = (async (): Promise<T> => loadConfigFn())();
    return config;
  };
}
